import { sortFloors } from '@utils/sortFloors';

/**
 * Pure derivations for the Activities & Issues right-rail panels.
 *
 * Inputs mirror the live Monitor selectors (`selectLiveSummary`,
 * `selectWorkforceRows`, `selectFloorSummaries`) plus the static-tag roster.
 * On-site / per-trade / per-floor figures are live presence data fed by the
 * Monitor SSE stream; "Planned" and "Cert" come from the roster. No mock data.
 */

export interface WorkforceVM {
  onSite: number;
  missing: number;
  attendancePct: number;
}

export interface TradeRowVM {
  id: string;
  trade: string;
  contractor: string;
  onSite: number;
  planned: number;
  cert: boolean;
  location: string;
}

export interface FloorVM {
  id: string;
  label: string;
  workers: number;
}

export interface OperationsPanels {
  workforce: WorkforceVM;
  trades: TradeRowVM[];
  floors: FloorVM[];
}

/** A static worker tag (assigned-workforce roster entry). */
export interface WorkerTagLike {
  professionId?: string | null;
  certificates?: Array<{ expirationDate?: string }>;
}

/** One row from `selectWorkforceRows` — a profession with on-site tags. */
export interface WorkforceRowLike {
  id: string;
  tradeId?: string;
  contractor?: string;
  onSite: number;
  floors: string[];
}

/** One row from `selectFloorSummaries`. */
export interface FloorSummaryLike {
  floorId: string;
  label: string;
  workersCount: number;
  isSpecial?: boolean;
}

/** Live Monitor data the panels derive from. */
export interface OperationsInput {
  /** Total workers on site (from `selectLiveSummary.workersOnSite`). */
  workersOnSite: number;
  workforceRows: readonly WorkforceRowLike[];
  floorSummaries: readonly FloorSummaryLike[];
  staticTags: readonly WorkerTagLike[];
}

/** A certificate counts as expired when its expiration date is in the past. */
function isExpired(c: { expirationDate?: string }, now: Date): boolean {
  if (!c.expirationDate) return false;
  const t = new Date(c.expirationDate).getTime();
  return Number.isFinite(t) && t < now.getTime();
}

/** Groups the worker roster by profession id. */
function rosterByProfession(staticTags: readonly WorkerTagLike[]): Map<string, WorkerTagLike[]> {
  const m = new Map<string, WorkerTagLike[]>();
  for (const tag of staticTags) {
    const id = tag.professionId ?? '';
    if (!id) continue;
    const arr = m.get(id) ?? [];
    arr.push(tag);
    m.set(id, arr);
  }
  return m;
}

export function deriveWorkforce(input: OperationsInput): WorkforceVM {
  const onSite = input.workersOnSite;
  // The assigned roster is the planned headcount; guard against a roster
  // smaller than the live on-site count (data skew) so "missing" never goes
  // negative.
  const planned = Math.max(input.staticTags.length, onSite);
  return {
    onSite,
    missing: Math.max(0, planned - onSite),
    attendancePct: planned > 0 ? Math.round((onSite / planned) * 100) : 0,
  };
}

export function deriveTrades(
  input: OperationsInput,
  resolveTradeLabel: (tradeId: string) => string,
  now: Date = new Date(),
): TradeRowVM[] {
  const roster = rosterByProfession(input.staticTags);
  const hasRoster = input.staticTags.length > 0;
  return input.workforceRows.map((r) => {
    const profRoster = roster.get(r.id) ?? [];
    return {
      id: r.id,
      trade: r.tradeId ? resolveTradeLabel(r.tradeId) : r.id,
      contractor: r.contractor ?? '',
      onSite: r.onSite,
      // Planned / cert come from the roster; with no roster, planned is the
      // on-site count and cert is treated as clear (no expiry data to flag).
      planned: hasRoster ? Math.max(profRoster.length, r.onSite) : r.onSite,
      cert: hasRoster
        ? profRoster.every((t) => !(t.certificates ?? []).some((c) => isExpired(c, now)))
        : true,
      location: r.floors[0] ?? '—',
    };
  });
}

export function deriveFloors(input: OperationsInput): FloorVM[] {
  // Order floors the way Trusstor does (utils/sortFloors): numeric by floor
  // id rather than lexical, highest floor first — matches the Dashboard.
  return sortFloors([...input.floorSummaries], 'desc').map((f) => ({
    id: f.floorId,
    label: f.label,
    workers: f.workersCount,
  }));
}

export function deriveOperationsPanels(
  input: OperationsInput,
  resolveTradeLabel: (tradeId: string) => string,
  now: Date = new Date(),
): OperationsPanels {
  return {
    workforce: deriveWorkforce(input),
    trades: deriveTrades(input, resolveTradeLabel, now),
    floors: deriveFloors(input),
  };
}
